/**
 * The Table's configurable columns. The Title column is always shown (it carries the tree/indent,
 * key, epic chip and inline title), so it isn't listed here — only the columns a member can toggle
 * and reorder-independent extras. Widths are grid tracks; `sortKey` (when present) wires the header
 * to the board page's sort state. Assignee/PdM are narrow now that the avatars render at ~24px.
 */
export type ColKey = 'status' | 'priority' | 'assignee' | 'pdm' | 'source' | 'labels' | 'start' | 'end';

export interface ColumnDef {
  key: ColKey;
  label: string;
  /** CSS grid track width for this column. */
  width: string;
  /** Item sort field for the column header (omitted = not sortable, e.g. PdM / Source). */
  sortKey?: string;
  align: 'center' | 'left';
}

/** Every toggle-able column, in their canonical display order. */
export const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'status', label: 'Status', width: '64px', sortKey: 'status', align: 'center' },
  { key: 'priority', label: 'Priority', width: '72px', sortKey: 'priority', align: 'center' },
  { key: 'assignee', label: 'Assignee', width: '84px', sortKey: 'assignee', align: 'center' },
  { key: 'pdm', label: 'PdM', width: '72px', align: 'center' },
  { key: 'source', label: 'Source', width: '150px', align: 'left' },
  { key: 'labels', label: 'Labels', width: '160px', align: 'left' },
  { key: 'start', label: 'Start', width: '96px', sortKey: 'start_date', align: 'left' },
  { key: 'end', label: 'End', width: '96px', sortKey: 'end_date', align: 'left' },
];

const BY_KEY = new Map<ColKey, ColumnDef>(TABLE_COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = new Set<ColKey>(TABLE_COLUMNS.map((c) => c.key));
const CANONICAL_ORDER: ColKey[] = TABLE_COLUMNS.map((c) => c.key);

/** Per-board table layout: the full ordered list of ALL columns (the Columns-menu row order) plus
 *  which of them are toggled off. The table renders `order` minus `hidden`, in `order` order. */
export interface ColumnState {
  order: ColKey[];
  hidden: ColKey[];
}

/** Fresh boards: canonical order, Source and Start hidden. */
export const DEFAULT_COLUMN_STATE: ColumnState = {
  order: [...CANONICAL_ORDER],
  hidden: ['source', 'start'],
};

const dedupe = (keys: ColKey[]): ColKey[] => {
  const seen = new Set<ColKey>();
  const out: ColKey[] = [];
  for (const k of keys) if (!seen.has(k)) (seen.add(k), out.push(k));
  return out;
};
const knownKeys = (raw: unknown): ColKey[] =>
  Array.isArray(raw) ? dedupe(raw.filter((k): k is ColKey => typeof k === 'string' && ALL_KEYS.has(k as ColKey))) : [];
/** `keys` (order preserved) followed by any known column it omits, in canonical order — so `order`
 *  always covers every column exactly once even after new columns are added to TABLE_COLUMNS. */
const withAllKeys = (keys: ColKey[]): ColKey[] => {
  const present = new Set(keys);
  return [...keys, ...CANONICAL_ORDER.filter((k) => !present.has(k))];
};

/** Coerce a persisted (untrusted) value into a valid ColumnState. Accepts the current
 *  `{order, hidden}` object AND the legacy bare-array shape (a visible-columns list). Guarantees
 *  `order` covers all columns and that at least one column stays visible. */
export const sanitizeColumnState = (raw: unknown): ColumnState => {
  // Legacy: a bare array was the visible columns in order — everything else was hidden.
  if (Array.isArray(raw)) {
    const vis = knownKeys(raw);
    const order = withAllKeys(vis);
    const visSet = new Set(vis);
    return { order, hidden: CANONICAL_ORDER.filter((k) => !visSet.has(k)) };
  }
  const obj = (raw ?? {}) as { order?: unknown; hidden?: unknown };
  const order = withAllKeys(knownKeys(obj.order));
  const hidden = new Set(knownKeys(obj.hidden));
  // Never hide every column — keep the first in order visible so the table is never blank.
  if (order.every((k) => hidden.has(k))) hidden.delete(order[0]);
  return { order, hidden: [...hidden] };
};

/** The visible columns, in display order (order minus hidden). */
export const visibleKeysOf = (state: ColumnState): ColKey[] =>
  state.order.filter((k) => !state.hidden.includes(k));

/** Resolve keys to their ColumnDefs, PRESERVING the given key order (so a reorder shows through). */
export const resolveColumns = (keys: ColKey[]): ColumnDef[] =>
  keys.map((k) => BY_KEY.get(k)).filter((c): c is ColumnDef => !!c);

/** The label for a column key (for the Columns menu). */
export const columnLabel = (key: ColKey): string => BY_KEY.get(key)?.label ?? key;

/** The gutter (#-rank / hover controls) width, mirrored from sortable-row's GUTTER_PX. */
const GUTTER_W = 128;
/** The Title column's min track. */
const TITLE_MIN = 240;

/** Grid template for the header + rows: an elastic Title column, then each visible column's track. */
export const gridTemplate = (columns: ColumnDef[]): string =>
  `minmax(${TITLE_MIN}px, 1fr) ${columns.map((c) => c.width).join(' ')}`;

/** Minimum width of the whole table (gutter + title + visible tracks) so it scrolls horizontally
 *  rather than crushing columns when many are shown. Widths are px strings like "72px". */
export const tableMinWidth = (columns: ColumnDef[]): number =>
  GUTTER_W + TITLE_MIN + columns.reduce((sum, c) => sum + (parseInt(c.width, 10) || 0), 0);
